// Libraries
import { Flex, FlexProps } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
// Components
import { NavLinkTabs } from '@atoms/index';
import { Dropdown } from '@molecules/index';
// Utils
import { AppPath, NavigationAuthorization } from '@constants/index';
import { vesselController } from '@controllers/index';
import { useAuthorization } from '@hooks/index';
import { CacheKeys } from '@utils/index';
import text from '@translations/en.json';
// Types
import { VesselDropdownOption, VesselNavbarRoute } from './VesselNavbar.type';

const routes: Record<string, VesselNavbarRoute> = {
  simulator: {
    label: text.vessel_navbar.cii_simulator,
    path: AppPath.CII_SIMULATOR,
  },
  performance: {
    label: text.vessel_navbar.vessel_performance,
    path: AppPath.FLEET_PERFORMANCE_ANALYSIS,
  },
};

/**
 * Represents the navigation bar for vessel-related pages.
 * Provides a dropdown to select a vessel and navigation links for different vessel-related pages.
 *
 * @component
 * @returns {ReactElement} The rendered VesselNavbar component.
 */
export const VesselNavbar = (props: FlexProps) => {
  const session = useSession();
  const clientId = session.data?.customer.id;
  const router = useRouter();
  const { getAuthorization } = useAuthorization(NavigationAuthorization);
  const imo = parseInt(router.query.imo as string, 10);
  const auth = getAuthorization(NavigationAuthorization.ciiFleetPerformanceNavigate);
  const [vessel, setVessel] = useState<VesselDropdownOption>();
  const { data: vessels = [] } = useQuery({
    queryKey: [CacheKeys.VESSELS, clientId],
    queryFn: vesselController,
    refetchOnWindowFocus: false,
    useErrorBoundary: true,
    enabled: !!clientId,
  });

  // Define the navigation tabs
  const tabs = useMemo(
    () =>
      Object.entries(routes)
        .map(([id, { label, path }]) => ({ id, label, href: `${path}/${imo}` }))
        .filter((tab) => tab.id !== 'performance' || auth.ciiFleetPerformanceNavigate),
    [imo, auth]
  );

  const activeTab = useMemo(() => tabs.find((tab) => router.asPath.startsWith(tab.href))?.id, [tabs, router.asPath]);

  const options: VesselDropdownOption[] = useMemo(
    () =>
      vessels
        .map((item) => ({ label: item.vesselName, value: item.vesselImo }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [vessels]
  );

  // Callback to update the vessel IMO when selecting an option from the dropdown
  const updateVesselImo = useCallback(
    (option: VesselDropdownOption) => {
      if (activeTab) {
        router.push(`${routes[activeTab as keyof typeof routes].path}/${option.value}`);
      }
    },
    [router, activeTab]
  );

  // Update the selected vessel when the IMO is set from router
  useEffect(() => {
    setVessel(options.find((option) => option.value == imo));
  }, [imo, setVessel, options]);

  return (
    <Flex as='nav' bg='white' w='full' justify='center' {...props}>
      <Flex
        w='full'
        justify='space-between'
        align={{ base: 'start', md: 'end' }}
        direction={{ base: 'column', md: 'row' }}
        px={{ base: 3, md: 6 }}
        pt={{ base: 3, md: 0 }}
        gap={4}
        maxW={384}
      >
        <Dropdown
          options={options}
          value={vessel}
          onChange={updateVesselImo}
          py={{ base: 0, md: 2 }}
          w={{ base: 'full', md: 72 }}
          data-testid='dropdown-vessel-selection'
        />
        <NavLinkTabs tabs={tabs} active={activeTab} />
      </Flex>
    </Flex>
  );
};
